/**
 * Enviar fotos — escolher, dizer de que dia (e, se quiser, de que
 * atividade), e acompanhar. Os estados são os do design: escolher · enviando
 * · concluído · com erro. A fila vive fora da tela: pode sair que o envio
 * continua enquanto o app estiver aberto.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useOnline } from '../../sync/useOnline';
import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Card, ScreenHeader, SectionLabel } from '../../ui/primitives';
import { ddmm, plural, useConfigMedia } from './comum';
import { resumoDaFila, useFila } from './fila';

interface AtividadeDoDia {
  id: string;
  title: string;
  startTime: string;
  deletedAt: string | null;
}

async function lerAtividades(dayId: string): Promise<AtividadeDoDia[]> {
  const res = await fetch(`/api/v1/days/${dayId}`, { credentials: 'same-origin' });
  if (!res.ok) return [];
  const corpo = (await res.json()) as { activities: AtividadeDoDia[] };
  return corpo.activities.filter((a) => !a.deletedAt);
}

export function EnviarScreen() {
  const { tripId } = useTrip();
  const [params] = useSearchParams();
  const dias = useDiasDaViagem(tripId);
  const online = useOnline();
  const config = useConfigMedia();
  const entrada = useRef<HTMLInputElement>(null);

  const hoje = new Date().toISOString().slice(0, 10);
  const diaInicial = params.get('dia') ?? (dias.days?.some((d) => d.date === hoje) ? hoje : (dias.days?.[0]?.date ?? ''));
  const [data, setData] = useState(diaInicial);
  const dataEfetiva = data || diaInicial;
  const dia = dias.days?.find((d) => d.date === dataEfetiva) ?? null;
  const [activityId, setActivityId] = useState<string>(params.get('atividade') ?? '');

  const atividades = useQuery({ queryKey: ['memorias', 'atividades', dia?.id], queryFn: () => lerAtividades(dia?.id ?? ''), enabled: !!dia });

  const todosOsItens = useFila((s) => s.itens);
  const fila = useMemo(() => todosOsItens.filter((i) => i.tripId === tripId), [todosOsItens, tripId]);
  const adicionar = useFila((s) => s.adicionar);
  const tentarDeNovo = useFila((s) => s.tentarDeNovo);
  const descartar = useFila((s) => s.descartar);
  const limparConcluidas = useFila((s) => s.limparConcluidas);
  const andamento = useMemo(() => resumoDaFila(fila), [fila]);

  function escolher(lista: FileList | null) {
    if (!lista || !dia) return;
    adicionar([...lista], { tripId, dayId: dia.id, activityId: activityId || null });
    if (entrada.current) entrada.current.value = '';
  }

  const bloqueado = config.isSuccess && !config.data.media;

  return (
    <section className="screen mem">
      <ScreenHeader title="Enviar fotos" />

      {bloqueado && <p className="auth-error">O envio de fotos não está ligado neste ambiente.</p>}

      <SectionLabel>DE QUE DIA</SectionLabel>
      <Card>
        <select className="field__input mem__select" value={dataEfetiva} onChange={(e) => { setData(e.target.value); setActivityId(''); }} aria-label="Dia">
          {(dias.days ?? []).map((d) => (
            <option key={d.id} value={d.date}>
              {ddmm(d.date)}{d.title ? ` · ${d.title}` : ''}
            </option>
          ))}
        </select>
        {atividades.isSuccess && atividades.data.length > 0 && (
          <select className="field__input mem__select" value={activityId} onChange={(e) => setActivityId(e.target.value)} aria-label="Atividade (opcional)">
            <option value="">Sem atividade específica</option>
            {atividades.data.map((a) => (
              <option key={a.id} value={a.id}>
                {a.startTime} · {a.title}
              </option>
            ))}
          </select>
        )}
      </Card>

      <input
        ref={entrada}
        type="file"
        accept="image/*"
        multiple
        hidden
        data-testid="mem-arquivos"
        onChange={(e) => escolher(e.target.files)}
      />
      <button
        type="button"
        className="nv-btn nv-btn--primary nv-btn--block"
        disabled={!dia || bloqueado}
        onClick={() => entrada.current?.click()}
      >
        <Icon name="memorias" size={18} />
        Escolher fotos
      </button>
      <p className="auth-note">As fotos são comprimidas no aparelho antes de subir. Você pode sair desta tela — o envio continua enquanto o app estiver aberto.</p>

      {fila.length > 0 && (
        <>
          <SectionLabel>
            {andamento.terminou
              ? andamento.erros === 0
                ? `${plural(andamento.concluidas, 'FOTO ENVIADA', 'FOTOS ENVIADAS')}`
                : `${andamento.erros} DE ${andamento.total} NÃO ${andamento.erros === 1 ? 'SUBIU' : 'SUBIRAM'}`
              : `ENVIANDO… ${andamento.concluidas} DE ${andamento.total}`}
          </SectionLabel>
          {!andamento.terminou && (
            <div className="mem__barra" role="progressbar" aria-valuemin={0} aria-valuemax={andamento.total} aria-valuenow={andamento.concluidas}>
              <span style={{ width: `${(andamento.concluidas / Math.max(1, andamento.total)) * 100}%` }} />
            </div>
          )}
          <Card>
            {fila.map((i) => (
              <div key={i.id} className={`mem__item mem__item--${i.estado}`}>
                <img src={i.previaUrl} alt="" className="mem__item-previa" />
                <span className="mem__item-texto">
                  <span className="mem__item-nome">{i.nome}</span>
                  <span className="mem__item-estado">
                    {i.estado === 'aguardando' && (online ? 'Na fila' : 'Na fila · sobe quando a rede voltar')}
                    {i.estado === 'comprimindo' && 'Comprimindo…'}
                    {i.estado === 'enviando' && 'Enviando…'}
                    {i.estado === 'concluida' && 'Enviada'}
                    {i.estado === 'erro' && (i.erro ?? 'Falhou')}
                  </span>
                </span>
                {i.estado === 'erro' && (
                  <span className="mem__item-acoes">
                    <button type="button" className="nv-btn nv-btn--chip" onClick={() => tentarDeNovo(i.id)}>
                      Tentar de novo
                    </button>
                    <button type="button" className="nv-btn nv-btn--chip" onClick={() => descartar(i.id)} aria-label="Deixar de fora">
                      ×
                    </button>
                  </span>
                )}
                {i.estado === 'concluida' && <Icon name="concluir" size={18} />}
              </div>
            ))}
          </Card>
          {andamento.terminou && andamento.concluidas > 0 && (
            <Link to={`/memorias/${dataEfetiva}`} className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => limparConcluidas()}>
              Ver nas Memórias
            </Link>
          )}
          {andamento.terminou && andamento.erros > 0 && (
            <p className="auth-note">As que falharam continuam aqui até você tentar de novo ou deixar de fora — nada foi perdido.</p>
          )}
        </>
      )}
    </section>
  );
}
